// tslint:disable:object-literal-sort-keys
/**
 * @file
 * Both Final Results Final and Final Results Day need table rows built out
 */

import filter from 'lodash/filter';
import flow from 'lodash/flow';
import get from 'lodash/get';
import keys from 'lodash/keys';
import map from 'lodash/map';
import max from 'lodash/max';
import memoize from 'lodash/memoize';
import range from 'lodash/range';
import startsWith from 'lodash/startsWith';
import * as React from 'react';

// Util
import { getProfileUrlById } from '../../config';
import { formatMoney, prettifyOunces } from '../../util/util';

// Types
import {
  IFinalResultsDayAngler,
  IFinalResultsDayRow,
  IFinalResultsFinalAngler,
  IFinalResultsFinalRow,
} from './types';

const buildName = (
  name: string,
  id: number,
  secondName?: string,
  secondId?: number,
  teamName?: string,
) => {
  let name1 = name;
  let name2 = secondName ?? '';
  let team = teamName ?? '';

  if (secondId && !secondName && name.indexOf(' - ') !== -1) {
    const parts = name.split(' - ');
    name1 = parts[0];
    name2 = parts[1];
    team = parts[2] ?? '';
  }
  return (
    <>
      <span key={id}>
        <a href={getProfileUrlById(id)}>{name1}</a>
      </span>
      {name2 ? (
        <span key={secondId}>
          {' & '}
          <a href={getProfileUrlById(secondId)}>{name2}</a>
        </span>
      ) : null}
      {team ? (
        <span className="team-name">{` - ${team}`}</span>
      ) : null}
    </>
  );
};

/**
 * Format a single row of the FinalResults table.
 */
export const buildFinalResultsFinalTableRow = ({
  final_weight,
  prize_money,
  merchandise_bonus,
  cash_bonus,
  totalWinnings,
  place,
  FishermenId,
  PartnerFishermenId,
  partnername,
  name,
  teamname
}: IFinalResultsFinalAngler): IFinalResultsFinalRow => ({
  Rank: <span>{place}</span>,
  Name: buildName(name, FishermenId, partnername, PartnerFishermenId, teamname),
  'Total Weight': (
    <span className="totalWeight">{prettifyOunces(final_weight, false)}</span>
  ),
  'Total Winnings ($)': (
    <span className="totalWinnings">{formatMoney(totalWinnings)}</span>
  ),
  'Cash Winnings ($)': (
    <span className="cashWinnings">{formatMoney(prize_money)}</span>
  ),
  'Merchandise Bonus ($)': (
    <span className="merchBonus">{formatMoney(merchandise_bonus)}</span>
  ),
  'Cash Bonus ($)': (
    <span className="cashBonus">{formatMoney(cash_bonus)}</span>
  ),
});

/**
 * Filter down to columns that start with 'FishCount_day'
 * @param dayKeys
 */
export const filterPerDayKeys = (dayKeys: string[]) =>
  filter(dayKeys, key => startsWith(key, 'FishCount_day'));

/**
 * Turn array of strings 'FishCount_dayX' to array of numbers X
 * @param dayKeys
 */
export const mapDayToNumber = (dayKeys: string[]) =>
  map(dayKeys, day => parseInt(day.replace('FishCount_day', ''), 10));

/**
 * Generates a function that runs an arg through a flow of processing functions
 */
export const makeMaxDayProcessor = flow([
  keys,
  filterPerDayKeys,
  mapDayToNumber,
  max,
]);

/**
 * Infer the maximum day from an entry
 * @param entry
 */
export const inferMaxDay = (entry: IFinalResultsDayAngler) =>
  makeMaxDayProcessor(entry);

/**
 * A memoized function that caches max days lookups so it can be called
 * repeatedly efficiently. Uses TournamentId as the cache key.
 */
export const maxDayLookup = memoize(inferMaxDay, e => e.TournamentId);

/**
 * Build row for per-day Final Results.
 */
export function buildFinalResultsDayTableRow(
  entry: IFinalResultsDayAngler,
  index: number
): IFinalResultsDayRow {
  const {
    FishermenId,
    TotalWeight,
    TotalMoney,
    TotalFish,
    PartnerFishermenId,
    Partnername,
    name,
    teamname
  } = entry;

  // Find max day from headers
  const maxDay = maxDayLookup(entry);

  // For each day leading up to current day, format per-day stats. These keys
  // add columns to the table.
  const perDay = maxDay
    ? range(1, maxDay + 1).reduce((obj: any, i) => {
        const fishCount = get(entry, `FishCount_day${i}`);

        const weightLbs = prettifyOunces(
          get(entry, `TotalOuncesAfterPenalty_day${i}`, 0),
          false
        );

        obj[`Day ${i}<br />Fish / Weight`] = (
          <span>
            <span className="count">{fishCount}</span>
            <span> / </span>
            <span className="weight">{weightLbs}</span>
          </span>
        );
        return obj;
      }, {})
    : {};

  return {
    Rank: <span className={(index + 1).toString()}>{index + 1}</span>,
    Name: buildName(name, FishermenId, Partnername, PartnerFishermenId, teamname),
    ...perDay,
    'Total Fish': (
      <span className="final-results-day__count">
        <span>{TotalFish}</span>
      </span>
    ),
    'Total Weight': (
      <span className="final-results-day__lbs">
        <span>{prettifyOunces(TotalWeight, false)}</span>
      </span>
    ),
    'Total Money($)': (
      <span className="final-results-day__money">
        <span>{formatMoney(TotalMoney)}</span>
      </span>
    ),
  };
}

/**
 * Final Results used on completed tournament pages for summary. Simply calls
 * buildFinalResultsDayTableRow, strips unused columns, adds new summary column.
 * @param entry
 * @param index
 */
export function buildFinalResultsMiniTableRow(
  entry: IFinalResultsDayAngler,
  index: number
) {
  // Render day results, but WITHOUT units in prettifyOunces()
  const miniRow = buildFinalResultsDayTableRow(entry, index);

  // Remove prior set keys
  delete miniRow['Total Fish'];
  delete miniRow['Total Weight'];
  delete miniRow['Total Money($)'];

  // New summary
  miniRow['Total<br/> Fish / weight'] = (
    <span className="final-results-day__fish-weight-total">
      <span>{entry.TotalFish}</span>
      <span> / </span>
      <span className="weight">
        <span>{prettifyOunces(entry.TotalWeight, false)}</span>
      </span>
    </span>
  );

  return miniRow;
}
